import type { StorefrontProductSummary } from '@erp/shared';
import { Check, Heart, Plus, Share2 } from 'lucide-react';
import { type CSSProperties, type MouseEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { useCartStore } from '../../lib/cartStore.js';
import { formatMoney } from '../../lib/money.js';
import { useWishlistStore } from '../../lib/wishlistStore.js';

import { rememberSaving } from './cartSavings.js';
import { DiscountBadge, OriginalPrice, resolveSaving } from './discountDisplay.js';
import { StorefrontShareModal } from './StorefrontShareModal.js';
import { StoreImage } from './StoreImage.js';

/**
 * Premium storefront product card (shared by home / catalog / category / related):
 * rounded image-on-top tile with a status pill, name, bold price, and a circular
 * quick-add button. Themed entirely from the tenant's brand tokens.
 */
export function ProductCard({ product, index = 0 }: { product: StorefrontProductSummary; index?: number }): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const add = useCartStore((s) => s.add);
  const toggleWish = useWishlistStore((s) => s.toggle);
  const saved = useWishlistStore((s) => s.slug === slug && s.items.includes(product.id));
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);
  const [added, setAdded] = useState(false);
  const addedTimer = useRef<number>();
  const to = `/s/${slug}/product/${product.slug ?? product.id}`;
  // An automatic discount is already baked into product.price — this is only what
  // it replaced, for the strikethrough and the badge.
  const saving = resolveSaving(product);

  const onWish = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    toggleWish(slug, product.id);
  };

  const onShare = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setShareOpen(true);
  };

  const quickAdd = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    add(slug, { itemId: product.id, name: product.name, price: product.price, currencyCode: product.currencyCode, imageUrl: product.imageUrl, quantity: 1 });
    // Carry the saving into the cart: the cart only stores the charged price.
    rememberSaving(slug, { itemId: product.id, price: product.price }, saving);
    setAdded(true);
    window.clearTimeout(addedTimer.current);
    addedTimer.current = window.setTimeout(() => setAdded(false), 1100);
    toast.success(t('cart.addedTitle'), {
      description: product.name,
      action: { label: t('cart.viewCart'), onClick: () => navigate(`/s/${slug}/cart`) },
    });
  };

  return (
    <>
    <Link
      to={to}
      style={{ ['--sf-delay']: `${Math.min(index, 11) * 45}ms` } as CSSProperties}
      className="sf-card group flex flex-col overflow-hidden rounded-2xl border border-border bg-bg-subtle transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 hover:border-brand/40 hover:shadow-lg active:scale-[0.99] motion-reduce:transform-none"
    >
      <div className="sf-shine relative aspect-square overflow-hidden bg-bg">
        <StoreImage src={product.imageUrl} alt={product.name} className="h-full w-full object-contain transition-transform duration-300 ease-out group-hover:scale-[1.05] motion-reduce:transform-none" />
        {/* Merchandising badge (start corner): Featured outranks New. */}
        {product.featured ? (
          <span className="absolute start-3 top-3 rounded-full bg-brand px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-wide text-brand-contrast shadow-sm">{t('store.shop.featured')}</span>
        ) : product.isNew ? (
          <span className="absolute start-3 top-3 rounded-full bg-success px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-wide text-fg-inverse shadow-sm">{t('store.shop.new')}</span>
        ) : null}
        {/* Discount badge — the image's bottom-start corner, right above the price
            it explains. Deliberately out of the top row so it neither displaces
            the Featured/New badge nor collides with the stock one opposite it. */}
        {saving && (
          <DiscountBadge
            percent={saving.percent}
            className="sf-pop absolute bottom-3 start-3 rounded-full bg-danger px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-wide text-fg-inverse shadow-sm"
          />
        )}
        {/* Stock badge (below the wishlist heart): Out of stock outranks Low stock. */}
        {!product.inStock ? (
          <span className="absolute end-3 top-12 rounded-full bg-fg/70 px-2.5 py-1 text-[0.6rem] font-semibold text-bg backdrop-blur">{t('store.shop.outOfStock')}</span>
        ) : product.lowStock ? (
          <span className="absolute end-3 top-12 rounded-full bg-danger px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-wide text-fg-inverse shadow-sm">{t('store.shop.lowStock')}</span>
        ) : null}
        {/* Wishlist heart (end corner). */}
        {site.wishlistEnabled && (
          <button
            type="button"
            onClick={onWish}
            aria-label={t('store.wishlist.toggle', { defaultValue: 'Save to wishlist' })}
            aria-pressed={saved}
            className="absolute end-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-bg/80 text-fg-muted shadow-sm backdrop-blur transition-[transform,color] duration-150 ease-out hover:scale-110 hover:text-danger active:scale-90 motion-reduce:transform-none"
          >
            <Heart className={`h-4 w-4 ${saved ? 'fill-danger text-danger' : ''}`} aria-hidden />
          </button>
        )}
      </div>
      <div className="flex flex-1 items-end justify-between gap-2 p-4">
        <div className="min-w-0">
          <div className="line-clamp-1 text-sm font-semibold leading-snug transition-colors group-hover:text-brand">{product.name}</div>
          {product.description && <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-fg-muted">{product.description}</p>}
          {/* wraps so a struck-through original never squeezes the live price */}
          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <span className="font-mono text-base font-bold tabular-nums">{formatMoney(product.price, { locale: i18n.language, currency: product.currencyCode })}</span>
            {saving && <OriginalPrice amount={saving.originalPrice} currency={product.currencyCode} className="font-mono text-xs tabular-nums text-fg-subtle" />}
            {product.unit && <span className="text-[0.7rem] font-medium text-fg-muted">/ {product.unit}</span>}
          </div>
        </div>
        <div className="flex shrink-0 items-end gap-2">
          <button
            type="button"
            onClick={onShare}
            aria-label={t('store.product.share', { defaultValue: 'Share' })}
            className="grid h-9 w-9 place-items-center rounded-full border border-border text-fg-muted transition-[transform,color,border-color] duration-150 ease-out hover:border-brand/40 hover:text-brand active:scale-90 motion-reduce:transform-none"
          >
            <Share2 className="h-4 w-4" aria-hidden />
          </button>
          {product.inStock && (
            <button
              type="button"
              onClick={quickAdd}
              aria-label={t('cart.add')}
              className={`grid h-9 w-9 place-items-center rounded-full text-accent-contrast shadow-sm transition-[transform,opacity,box-shadow,background-color] duration-150 ease-out hover:scale-110 hover:opacity-90 hover:shadow-md active:scale-90 motion-reduce:transform-none ${added ? 'bg-success' : 'bg-accent'}`}
            >
              {added ? <Check key="check" className="sf-pop h-4 w-4" aria-hidden /> : <Plus className="h-4 w-4" aria-hidden />}
            </button>
          )}
        </div>
      </div>
    </Link>
    <StorefrontShareModal slug={slug} product={product} open={shareOpen} onClose={() => setShareOpen(false)} />
    </>
  );
}
